import { useEffect, useState } from "react";
import axios from "axios";
import {
  Paper, Typography, TextField, MenuItem, Button, Stack, Box, Chip, Divider,
  IconButton, Autocomplete, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Tooltip, ToggleButton, ToggleButtonGroup, CircularProgress, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DownloadIcon from "@mui/icons-material/Download";
import { exportGuidelinePdf } from "../exportGuidelinePdf";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { API_BASE_URL } from "../config";
import { useMe } from "../MeContext";
import { useFullScreenDialog } from "../useFullScreenDialog";
import { useWriteLock } from "../useWriteLock";
import { notify } from "../notify";
import {
  GUIDELINE_STATUSES, GUIDELINE_STATUS_COLOR, GUIDELINE_LINK_LISTS, GUIDELINES_DISCLAIMER,
} from "../constants";
import { RichTextEditor, GUIDELINE_PHOTOS_API } from "../components/RichText";
import GuidelineDocument from "../components/GuidelineDocument";
import LinkManager from "../components/LinkManager";

const API = `${API_BASE_URL}/guidelines`;
const PERSONNEL_API = `${API_BASE_URL}/personnel`;

const BLANK = {
  title: "",
  category: "",
  version: "",
  status: "Draft",
  last_review_date: "",
  author_personnel_id: null,
  reviewer_personnel_id: null,
  introduction: "",
  sections: [],
  references: [],
};

const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString();
};

const personName = (p) => `${p.forename || ""} ${p.surname || ""}`.trim();

// Increment the trailing run of digits in a version string, preserving any
// prefix/suffix: "v1" -> "v2", "3.4" -> "3.5", "version 3" -> "version 4".
// Left unchanged when there are no digits to bump.
const bumpVersion = (v) => {
  const s = v || "";
  const m = s.match(/(\d+)(\D*)$/);
  if (!m) return s;
  return s.slice(0, m.index) + String(Number(m[1]) + 1) + m[2];
};

// Autocomplete option label — name plus email to disambiguate people while picking.
const personLabel = (p) => personName(p) + (p.email ? ` (${p.email})` : "");

// Move item at index `i` by `delta` (±1) within a list, returning a new array.
const moveItem = (list, i, delta) => {
  const j = i + delta;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

export default function Guidelines() {
  const { can } = useMe();
  const fullScreen = useFullScreenDialog();
  const canEdit = can("Guidelines.edit");
  const canDelete = can("Guidelines.delete");
  const canAdd = can("Guidelines.add");

  const [guidelines, setGuidelines] = useState([]);
  const [loading, setLoading] = useState(true); // true until the first guidelines fetch resolves
  const [people, setPeople] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [mode, setMode] = useState("edit");      // dialog view: "edit" | "preview"
  const [form, setForm] = useState(BLANK);
  const [initialForm, setInitialForm] = useState(BLANK);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Save posts a new guideline, so a double-tap would file two (see useWriteLock).
  const { busy: saving, run: runWrite } = useWriteLock();

  const load = () => axios.get(API + "/").then(res => setGuidelines(res.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    axios.get(PERSONNEL_API + "/").then(res => setPeople(res.data || [])).catch(() => setPeople([]));
  }, []);

  const toForm = (g) => ({
    title: g.title ?? "",
    category: g.category ?? "",
    version: g.version ?? "",
    status: g.status ?? "Draft",
    last_review_date: g.last_review_date ? g.last_review_date.slice(0, 10) : "",
    author_personnel_id: g.author_personnel_id ?? null,
    reviewer_personnel_id: g.reviewer_personnel_id ?? null,
    introduction: g.introduction ?? "",
    sections: (g.sections || []).map(s => ({ title: s.title ?? "", content: s.content ?? "" })),
    references: (g.references || []).map(r => ({ title: r.title ?? "", url: r.url ?? "" })),
  });

  const openNew = () => {
    setEditing("new");
    setMode("edit");
    setForm(BLANK);
    setInitialForm(BLANK);
  };

  const openEdit = (g) => {
    // Fetch the full record (list rows carry every field already, but this keeps
    // the editor in step with the server if a field is ever list-trimmed).
    axios.get(`${API}/${g.id}`).then(res => {
      const loaded = toForm(res.data);
      setEditing(g.id);
      setMode("edit");
      setForm(loaded);
      setInitialForm(loaded);
    });
  };

  // Duplicate: create a Draft copy with an incremented version, then open it for
  // editing. All other content is copied verbatim from the source.
  const duplicate = (g) => runWrite(() =>
    axios.get(`${API}/${g.id}`).then(res => {
      const src = toForm(res.data);
      const copy = { ...src, status: "Draft", version: bumpVersion(src.version) };
      return axios.post(API + "/", {
        ...copy,
        title: copy.title.trim(),
        category: copy.category.trim() || null,
        version: copy.version.trim() || null,
        last_review_date: copy.last_review_date || null,
        introduction: copy.introduction || null,
      }).then(created => {
        notify("Guideline duplicated.", "success");
        load();
        const loaded = toForm(created.data);
        setEditing(created.data.id);
        setMode("edit");
        setForm(loaded);
        setInitialForm(loaded);
      });
    }));

  const close = () => { setEditing(null); setConfirmClose(false); };
  const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialForm);
  const requestClose = () => (canEdit && isDirty() ? setConfirmClose(true) : close());

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const payloadOf = () => ({
    title: form.title.trim(),
    category: form.category.trim() || null,
    version: form.version.trim() || null,
    status: form.status,
    last_review_date: form.last_review_date || null,
    author_personnel_id: form.author_personnel_id ?? null,
    reviewer_personnel_id: form.reviewer_personnel_id ?? null,
    introduction: form.introduction || null,
    sections: form.sections,
    references: form.references,
  });

  const save = () => runWrite(() => {
    const payload = payloadOf();
    const req = editing === "new"
      ? axios.post(API + "/", payload)
      : axios.put(`${API}/${editing}`, payload);
    return req.then(() => {
      notify(editing === "new" ? "Guideline created." : "Guideline saved.", "success");
      load();
      close();
    });
  });

  const doDelete = () => {
    axios.delete(`${API}/${editing}`).then(() => {
      notify("Guideline deleted.", "success");
      setConfirmDelete(false);
      load();
      close();
    });
  };

  // --- Section / reference helpers ---
  const setSections = (sections) => setForm(f => ({ ...f, sections }));
  const setReferences = (references) => setForm(f => ({ ...f, references }));
  const addSection = () => setSections([...form.sections, { title: "", content: "" }]);
  const addReference = () => setReferences([...form.references, { title: "", url: "" }]);
  const updateSection = (i, key, value) =>
    setSections(form.sections.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)));
  const updateReference = (i, key, value) =>
    setReferences(form.references.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));

  const authorValue = people.find(p => p.id === form.author_personnel_id) || null;
  const reviewerValue = people.find(p => p.id === form.reviewer_personnel_id) || null;
  const canSave = form.title.trim().length > 0;
  const viewing = editing !== null && !canEdit; // read-only mode for non-editors
  // Show the User-view render when a non-editor is viewing, or an editor has
  // flipped the toggle to Preview.
  const showDoc = viewing || mode === "preview";
  // Preview uses name only (no email) — matching the server-side name snapshot
  // shown in the User view.
  const previewGuideline = {
    ...form,
    author_name: authorValue && personName(authorValue),
    reviewer_name: reviewerValue && personName(reviewerValue),
  };

  const doExport = () => {
    setExporting(true);
    Promise.resolve(exportGuidelinePdf(previewGuideline)).finally(() => setExporting(false));
  };

  // The three lists in the order the user-facing page shows them.
  const [policies, publics] = GUIDELINE_LINK_LISTS;
  const linkProps = { canAdd, canEdit, canDelete };

  return (
    <Stack spacing={4}>
      <Typography variant="h4">Guidelines</Typography>

      <Box>
        <Typography variant="h6" gutterBottom>{policies.heading}</Typography>
        <LinkManager kind={policies.kind} empty={policies.empty}
                     addLabel={policies.addLabel} {...linkProps} />
      </Box>

      <Box>
        <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Shelter Stone clinical guidelines</Typography>
          {canAdd && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>Create new</Button>
          )}
        </Stack>
        <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "action.hover" } }}>
                  <TableCell>Category</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last review</TableCell>
                  {canAdd && <TableCell align="right" />}
                </TableRow>
              </TableHead>
              <TableBody>
                {guidelines.map(g => (
                  <TableRow hover key={g.id} onClick={() => openEdit(g)} sx={{ cursor: "pointer" }}>
                    <TableCell>{g.category || "—"}</TableCell>
                    <TableCell>{g.title}</TableCell>
                    <TableCell>{g.version || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={g.status}
                            color={GUIDELINE_STATUS_COLOR[g.status] || "default"} />
                    </TableCell>
                    <TableCell>{fmtDate(g.last_review_date)}</TableCell>
                    {canAdd && (
                      <TableCell align="right" padding="checkbox">
                        <Tooltip title="Duplicate">
                          <IconButton size="small" aria-label={`Duplicate ${g.title}`} disabled={saving}
                                      onClick={(e) => { e.stopPropagation(); duplicate(g); }}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {guidelines.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canAdd ? 6 : 5} align="center" sx={{ color: "text.secondary", py: 4 }}>
                      {loading ? <CircularProgress size={28} /> : "No guidelines yet."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Box>
        <Typography variant="h6" gutterBottom>{publics.heading}</Typography>
        <LinkManager kind={publics.kind} empty={publics.empty}
                     addLabel={publics.addLabel} {...linkProps} />
      </Box>

      {/* Fixed text — shown to users at the foot of their Guidelines page. */}
      <Alert severity="info" icon={false}>{GUIDELINES_DISCLAIMER}</Alert>

      {/* Editor / viewer dialog */}
      <Dialog open={editing !== null} onClose={requestClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box component="span" sx={{ flexGrow: 1 }}>
            {editing === "new" ? "New guideline" : viewing ? "Guideline" : "Edit guideline"}
          </Box>
          {canEdit && (
            <ToggleButtonGroup size="small" exclusive value={mode}
                               onChange={(_, m) => m && setMode(m)} aria-label="Editor mode">
              <ToggleButton value="edit">Edit</ToggleButton>
              <ToggleButton value="preview">Preview</ToggleButton>
            </ToggleButtonGroup>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {showDoc ? (
            <GuidelineDocument guideline={previewGuideline} showStatus />
          ) : (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Title" fullWidth required value={form.title} onChange={set("title")} />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField label="Category" fullWidth value={form.category} onChange={set("category")} />
                <TextField label="Version" fullWidth value={form.version} onChange={set("version")} />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField label="Status" select fullWidth value={form.status} onChange={set("status")}>
                  {GUIDELINE_STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </TextField>
                <TextField label="Last review date" type="date" fullWidth
                           slotProps={{ inputLabel: { shrink: true } }}
                           value={form.last_review_date} onChange={set("last_review_date")} />
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Autocomplete fullWidth options={people} getOptionLabel={personLabel} value={authorValue}
                              isOptionEqualToValue={(o, v) => o.id === v.id}
                              onChange={(_, v) => setForm({ ...form, author_personnel_id: v ? v.id : null })}
                              renderInput={(p) => <TextField {...p} label="Author" />} />
                <Autocomplete fullWidth options={people} getOptionLabel={personLabel} value={reviewerValue}
                              isOptionEqualToValue={(o, v) => o.id === v.id}
                              onChange={(_, v) => setForm({ ...form, reviewer_personnel_id: v ? v.id : null })}
                              renderInput={(p) => <TextField {...p} label="Reviewer" />} />
              </Stack>

              <Box>
                <Typography variant="subtitle2" gutterBottom>Introduction</Typography>
                <RichTextEditor value={form.introduction} uploadUrl={GUIDELINE_PHOTOS_API}
                                onChange={(v) => setForm(f => ({ ...f, introduction: v }))} />
              </Box>

              {/* Sections */}
              <Divider textAlign="left">Sections</Divider>
              {form.sections.map((s, i) => (
                <Paper key={i} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
                    <TextField label={`Section ${i + 1} title`} size="small" fullWidth value={s.title}
                               onChange={e => updateSection(i, "title", e.target.value)} />
                    <Tooltip title="Move up"><span>
                      <IconButton size="small" disabled={i === 0}
                                  onClick={() => setSections(moveItem(form.sections, i, -1))}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton></span>
                    </Tooltip>
                    <Tooltip title="Move down"><span>
                      <IconButton size="small" disabled={i === form.sections.length - 1}
                                  onClick={() => setSections(moveItem(form.sections, i, 1))}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton></span>
                    </Tooltip>
                    <Tooltip title="Delete section"><span>
                      <IconButton size="small" color="error"
                                  onClick={() => setSections(form.sections.filter((_, idx) => idx !== i))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton></span>
                    </Tooltip>
                  </Stack>
                  <RichTextEditor value={s.content} uploadUrl={GUIDELINE_PHOTOS_API}
                                  onChange={(v) => updateSection(i, "content", v)} />
                </Paper>
              ))}
              <Button startIcon={<AddIcon />} onClick={addSection} sx={{ alignSelf: "flex-start" }}>
                Add section
              </Button>

              {/* References */}
              <Divider textAlign="left">References</Divider>
              {form.references.map((r, i) => (
                <Stack key={i} direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ alignItems: "center" }}>
                  <TextField label="Title" size="small" fullWidth value={r.title}
                             onChange={e => updateReference(i, "title", e.target.value)} />
                  <TextField label="URL (optional)" size="small" fullWidth value={r.url}
                             onChange={e => updateReference(i, "url", e.target.value)} />
                  <Stack direction="row">
                    <Tooltip title="Move up"><span>
                      <IconButton size="small" disabled={i === 0}
                                  onClick={() => setReferences(moveItem(form.references, i, -1))}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton></span>
                    </Tooltip>
                    <Tooltip title="Move down"><span>
                      <IconButton size="small" disabled={i === form.references.length - 1}
                                  onClick={() => setReferences(moveItem(form.references, i, 1))}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton></span>
                    </Tooltip>
                    <Tooltip title="Delete reference"><span>
                      <IconButton size="small" color="error"
                                  onClick={() => setReferences(form.references.filter((_, idx) => idx !== i))}>
                        <DeleteIcon fontSize="small" />
                      </IconButton></span>
                    </Tooltip>
                  </Stack>
                </Stack>
              ))}
              <Button startIcon={<AddIcon />} onClick={addReference} sx={{ alignSelf: "flex-start" }}>
                Add reference
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {canDelete && editing !== "new" && !viewing && (
            <Button color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}
                    sx={{ mr: "auto" }}>Delete</Button>
          )}
          <Button onClick={doExport} disabled={exporting}
                  startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}>
            Export PDF
          </Button>
          <Button onClick={requestClose}>{viewing ? "Close" : "Cancel"}</Button>
          {canEdit && !viewing && (
            <Button variant="contained" disabled={!canSave || saving} onClick={save}
                    startIcon={saving ? <CircularProgress size={16} /> : undefined}>
              {saving ? "Saving…" : "Save"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Unsaved-changes confirmation */}
      <Dialog open={confirmClose} onClose={() => setConfirmClose(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes to this guideline. If you close now, they will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmClose(false)}>Keep editing</Button>
          <Button color="error" variant="contained" onClick={close}>Discard changes</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete guideline?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete "{form.title}". This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" startIcon={<DeleteIcon />} onClick={doDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
